import React, { useEffect, useState } from "react";
import { SubmitHandler, useFieldArray, useForm, useWatch } from "react-hook-form";
import { AlertCircle, Plus, Star, Trash2 } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getEditMaterial,
  getIsMaterialModalOpen,
  toggleMaterialModal,
} from "../../features/materials/materialsSlice";
import { IFormInput } from "../../types/material";
import { t } from "../../locales";
import { SIZE_UNIT } from "../../lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

const modalInputClass = (hasError: boolean) =>
  `h-12 text-base border-2 rounded-lg transition-all duration-200 ${
    hasError
      ? "border-red-500 focus:border-red-500 focus:shadow-focus-error"
      : "border-gray-200 focus:border-[#113F67] focus:shadow-focus-brand"
  }`;

const modalTextareaClass = (hasError: boolean) =>
  `text-base border-2 rounded-lg transition-all duration-200 ${
    hasError
      ? "border-red-500 focus:border-red-500 focus:shadow-focus-error"
      : "border-gray-200 focus:border-[#113F67] focus:shadow-focus-brand"
  }`;

const emptySize = { size: "", description: "", is_default: false };

type Props = {
  handleSaveMaterial: (data: IFormInput) => void;
};

const MaterialFormModal = ({ handleSaveMaterial }: Props) => {
  const dispatch = useAppDispatch();
  const editMaterial = useAppSelector(getEditMaterial);
  const isMaterialModalOpen = useAppSelector(getIsMaterialModalOpen);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    handleSubmit,
    register,
    control,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<IFormInput>({
    defaultValues: { name: "", description: "", sizes: [{ ...emptySize, is_default: true }] },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "sizes" });
  // fields[].is_default is a snapshot from append/reset, not live — watch
  // for the value setValue writes when a row is marked default.
  const watchedSizes = useWatch({ control, name: "sizes" });

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data: any) => {
    setIsLoading(true);
    await handleSaveMaterial(data);
    handleCloseFormModal();
    setIsLoading(false);
  };

  const handleCloseFormModal = () => {
    reset();
    dispatch(toggleMaterialModal(false));
  };

  useEffect(() => {
    if (editMaterial && editMaterial.id) {
      reset({
        name: editMaterial.name || "",
        description: editMaterial.description || "",
        sizes:
          editMaterial.sizes && editMaterial.sizes.length > 0
            ? editMaterial.sizes.map((s) => ({
                id: s.id,
                size: String(s.size),
                description: s.description || "",
                is_default: !!s.is_default,
              }))
            : [{ ...emptySize, is_default: true }],
      });
    } else {
      reset({ name: "", description: "", sizes: [{ ...emptySize, is_default: true }] });
    }
  }, [editMaterial, isMaterialModalOpen]);

  // Only one row can be default. Selecting one clears the flag on the rest;
  // with a single row it's implicitly the default and the control is hidden.
  const markDefault = (index: number) => {
    const sizes = getValues("sizes");
    sizes.forEach((_, i) => setValue(`sizes.${i}.is_default`, i === index));
  };

  return (
    <Dialog
      open={isMaterialModalOpen}
      onOpenChange={(open) => {
        if (!open) handleCloseFormModal();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#113F67]">
            {t.materials.formTitle}
          </DialogTitle>
          <DialogDescription>
            {editMaterial ? `Editing: ${editMaterial.name}` : t.materials.addNewMaterial}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-4 flex-1 flex flex-col sm:block pb-16 sm:pb-0 sm:space-y-5">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-semibold text-[#113F67]">{t.materials.nameLabel} <span className="text-red-500">*</span></Label>
            <Input
              id="name"
              placeholder={t.materials.namePlaceholder}
              {...register("name", { required: true })}
              className={modalInputClass(!!errors.name)}
            />
            {errors.name && errors.name.type === "required" && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />{t.materials.nameRequired}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-semibold text-[#113F67]">{t.materials.descriptionLabel}</Label>
            <Textarea
              id="description"
              rows={2}
              placeholder={t.materials.descriptionPlaceholder}
              {...register("description", { required: false })}
              className={modalTextareaClass(false)}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-semibold text-[#113F67]">
              {t.materials.sizesLabel} ({SIZE_UNIT}) <span className="text-red-500">*</span>
            </Label>

            {fields.map((field, index) => (
              <div
                key={field.id}
                className="rounded-lg p-3 bg-gray-50/50 border-l-4 border-[#34699A] space-y-2"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-[#113F67]">
                    {t.materials.sizeNumber.replace("{n}", String(index + 1))}
                  </span>
                  {fields.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => remove(index)}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> {t.materials.removeSize}
                    </Button>
                  )}
                </div>

                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder={t.materials.sizePlaceholder}
                      {...register(`sizes.${index}.size`, {
                        required: t.materials.sizeRequired,
                        pattern: {
                          value: /^\d+(\.\d)?$/,
                          message: t.materials.sizePatternError,
                        },
                      })}
                      onKeyDown={(e) => {
                        if (
                          ["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)
                        ) {
                          return;
                        }
                        if (e.key === ".") {
                          if ((e.target as HTMLInputElement).value.includes(".")) {
                            e.preventDefault();
                          }
                          return;
                        }
                        if (!/^\d$/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      className={modalInputClass(!!errors.sizes?.[index]?.size)}
                    />
                    {errors.sizes?.[index]?.size && (
                      <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                        <AlertCircle className="h-3 w-3" />{errors.sizes[index]?.size?.message}
                      </p>
                    )}
                  </div>

                  {fields.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      title={t.materials.defaultHint}
                      className={`h-12 px-3 rounded-lg border-2 whitespace-nowrap ${
                        watchedSizes?.[index]?.is_default
                          ? "border-[#113F67] bg-[#113F67] text-white hover:bg-[#0d2f4f] hover:text-white"
                          : "border-gray-200 text-gray-500 hover:text-[#113F67]"
                      }`}
                      onClick={() => markDefault(index)}
                    >
                      <Star className={`h-4 w-4 mr-1.5 ${watchedSizes?.[index]?.is_default ? "fill-white" : ""}`} />
                      {t.materials.defaultLabel}
                    </Button>
                  )}
                </div>

                <Input
                  placeholder={t.materials.sizeDescriptionPlaceholder}
                  {...register(`sizes.${index}.description`)}
                  className={modalInputClass(false)}
                />
              </div>
            ))}

            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full h-10 rounded-lg border-dashed border-2 border-gray-300 text-[#113F67] hover:bg-gray-50"
              onClick={() => append({ ...emptySize })}
            >
              <Plus className="h-4 w-4 mr-2" /> {t.materials.addSizeButton}
            </Button>
          </div>

          <div className="fixed bottom-0 left-0 right-0 flex gap-3 p-4 justify-end border-t bg-white z-10 sm:static sm:p-0 sm:pt-2 sm:z-auto sm:border-t sm:bg-transparent">
            <Button
              type="button"
              variant="outline"
              className="h-11 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
              onClick={handleCloseFormModal}
              disabled={isLoading}
            >
              {t.materials.cancelButton}
            </Button>
            <Button
              type="submit"
              className="h-11 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
              disabled={isLoading}
            >
              {isLoading ? t.materials.saving : t.materials.saveButton}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MaterialFormModal;
